/**
 * i18n provider — lightweight React context + JSON translation files.
 * No external library dependency. Add new locales by:
 * 1. Creating apps/web/src/i18n/<locale>.json matching the shape of en.json
 * 2. Adding the locale to the Locale union in types.ts
 * 3. Adding it to LOCALE_LABELS and the loaders map below
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  createElement,
  type ReactNode,
} from 'react'
import type { I18nContextValue, Locale, TranslationKey, TranslationParams } from './types'
import en from './en.json'

// ─── Translation loader ───────────────────────────────────────────────────────
// English is bundled; other locales are lazy-loaded on demand.

type TranslationDict = Record<string, unknown>

const cache = new Map<Locale, TranslationDict>()
cache.set('en', en as TranslationDict)

async function loadLocale(locale: Locale): Promise<TranslationDict> {
  if (cache.has(locale)) return cache.get(locale)!
  try {
    // Dynamic import — bundler will split each locale into its own chunk
    const mod = await import(`./${locale}.json`)
    const dict = (mod.default ?? mod) as TranslationDict
    cache.set(locale, dict)
    return dict
  } catch {
    // Locale file missing — fall back to English so the app never breaks
    return en as TranslationDict
  }
}

// ─── Key resolver ─────────────────────────────────────────────────────────────

function resolvePath(dict: TranslationDict, key: string): string | undefined {
  const parts = key.split('.')
  let node: unknown = dict
  for (const part of parts) {
    if (node === null || typeof node !== 'object') return undefined
    node = (node as Record<string, unknown>)[part]
  }
  return typeof node === 'string' ? node : undefined
}

// ─── Interpolation ────────────────────────────────────────────────────────────
// Replaces {{param}} placeholders. Example: t('stream.viewers', { count: 42 })
// → '42 viewers' when en.json has "viewers": "{{count}} viewers"

function interpolate(template: string, params: TranslationParams): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, name) => {
    const value = params[name]
    return value !== undefined ? String(value) : `{{${name}}}`
  })
}

// ─── Storage key ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'kast-locale'

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && /^[a-z]{2}$/.test(stored)) return stored as Locale
  } catch {
    // localStorage unavailable
  }
  return 'en'
}

// ─── Context ──────────────────────────────────────────────────────────────────

const I18nContext = createContext<I18nContextValue | null>(null)

// ─── Provider ─────────────────────────────────────────────────────────────────

interface I18nProviderProps {
  children: ReactNode
  /** Override the initial locale (e.g. from user profile). Defaults to localStorage / 'en'. */
  initialLocale?: Locale
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? readStoredLocale)
  const [dict, setDict] = useState<TranslationDict>(en as TranslationDict)

  // Load translation dict whenever locale changes
  useEffect(() => {
    loadLocale(locale).then(setDict)
    try {
      localStorage.setItem(STORAGE_KEY, locale)
    } catch {
      // ignore
    }
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
  }, [])

  const t = useCallback(
    (key: TranslationKey, params?: TranslationParams): string => {
      const raw = resolvePath(dict, key)
      if (raw === undefined) {
        // In development, surface missing keys visibly
        if (import.meta.env.DEV) {
          console.warn(`[i18n] Missing translation key: "${key}" (locale: ${locale})`)
        }
        // Fall back to the key itself — always renders something readable
        return key
      }
      return params ? interpolate(raw, params) : raw
    },
    [dict, locale],
  )

  return createElement(I18nContext.Provider, { value: { locale, setLocale, t } }, children)
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export { type I18nContextValue, type Locale, type TranslationKey, type TranslationParams }
export { LOCALE_LABELS } from './types'

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return ctx
}
